/**
 * R58-W-01 → SB2-W-04 — Parks & Sites country filter, backend-driven.
 *
 * Replaces the prior pattern of distinct-counting the rendered page
 * (`uniq(items.map(i => i.countryCode))`) with a dedicated facet
 * endpoint that returns every country code the tenant has at least
 * one non-soft-deleted site in.
 *
 * Contract pin: `assets/v1@4.4.0`, operation `listSitesCountryFacet`
 * (GET `/api/assets/v1/sites/facets/country`).
 *
 * 4.4.0 / SB2-W-04 widens each item from `{ countryCode, count }` to
 * `{ countryCode, count, alpha2?, alpha3?, englishName? }`. The
 * backend always populates the three new optional fields when emitting
 * 4.4.0+ — they're optional in the contract so a 4.2.0/4.3.0 client
 * (e.g. a Storybook fixture or a stale mock) still type-checks. The
 * FE consumes `englishName` directly as the dropdown label when
 * present; the previous `iso3166.ts` lookup is the fallback.
 *
 * SB2-W-04 also fixed the 5xx that prompted this rewire: a single bad
 * seed row (`Sheringham Shoal` with `country_code = '10'`) was leaking
 * into the 4.2.0 response and breaking the FE's flag/locale lookup.
 * The 4.4.0 BE inner-joins `public.global_countries` so non-alpha-2
 * codes can't reach the wire any more.
 *
 * Caching policy:
 *   - `staleTime: 5 min`   — matches the BE `Cache-Control: max-age=300, private`
 *     header on the 200 response. Reopening the dropdown inside the
 *     window therefore fires zero network calls (R58-W-01 AC7).
 *   - `gcTime: 30 min`     — keep the cached payload longer than the
 *     stale window so navigating away + back doesn't refetch.
 *
 * Tenant scope is implicit — `assetsFetch` injects the active
 * `X-Tenant-ID` header from the auth store. The TanStack key
 * therefore omits an explicit `tenantId` segment; when the user
 * switches tenants the auth store flushes the query cache via the
 * existing umbrella tenant-switch hook, so cross-tenant bleed is
 * structurally impossible.
 */
import { useQuery } from "@tanstack/react-query";
import { assetsFetch } from "./assetsFetch.js";
import { sitesKeys } from "./keys.js";

/**
 * Wire-shape per `SiteCountryFacetItem` in the 4.4.0 contract.
 *
 * `countryCode` + `count` REQUIRED (back-compat with 4.2.0/4.3.0).
 * `alpha2`, `alpha3`, `englishName` OPTIONAL on the contract; always
 * present in practice when the BE is at 4.4.0+.
 */
export interface CountryFacetItem {
  countryCode: string;
  count: number;
  alpha2?: string;
  alpha3?: string;
  englishName?: string;
}

/** Wire-shape per `SiteCountryFacetResponse` in the 4.4.0 contract. */
interface SiteCountryFacetResponse {
  items: CountryFacetItem[];
}

const FIVE_MIN_MS = 5 * 60_000;
const THIRTY_MIN_MS = 30 * 60_000;

/**
 * TanStack Query hook returning the active tenant's distinct
 * non-soft-deleted country codes (plus a count + resolved alpha-3 +
 * English name per code, when the BE is at 4.4.0+).
 *
 * Empty countries are excluded server-side per OQ-3 → option a /
 * OQ-SB2-C2(a) (reaffirmed).
 *
 * The response items are sorted by `englishName` ASC by the BE as of
 * 4.4.0 (was `countryCode` ASC in 4.2.0/4.3.0). The UI is free to
 * re-sort by locale-aware collation on render.
 */
export function useCountryFacet() {
  return useQuery<SiteCountryFacetResponse, Error, CountryFacetItem[]>({
    queryKey: [...sitesKeys.all, "facets", "country"] as const,
    queryFn: () =>
      assetsFetch<SiteCountryFacetResponse>("/sites/facets/country"),
    select: (data) => data.items,
    staleTime: FIVE_MIN_MS,
    gcTime: THIRTY_MIN_MS,
    retry: false,
  });
}
